import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import './App.css';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription } from './components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './components/ui/dialog';
import { Car, Users, Building2, Calendar, CreditCard, Menu, X, LogOut, Plus, Edit, Trash2, Eye, EyeOff, CheckCircle, XCircle, Clock, MapPin, Phone, Mail, Upload, Image as ImageIcon, Trash, Bell, AlertTriangle, AlertCircle, Building, Settings, DollarSign, RefreshCw, Filter, MessageSquare, Key, UserX, Ban, Send, ChevronLeft, ChevronRight, ChevronDown, BarChart3, TrendingUp, Star, User, History, FileText, FileCheck, Shield, Check, Wallet, Activity } from 'lucide-react';
import api from './api/client';
import { Toaster, toast } from 'sonner';
import { PageLoader, ButtonLoader } from './components/LoadingStates';
import { showErrorToast, showSuccessToast, showLoadingToast } from './lib/errorHandler';
import EnhancedAgencyDashboard from './components/agency/EnhancedAgencyDashboard';

// Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailVerificationNeeded, setEmailVerificationNeeded] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/api/auth/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const loadingToast = showLoadingToast('Connexion en cours...');
    
    try {
      const response = await api.post('/api/auth/login', { email, password });
      const { access_token, user } = response.data;
      
      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(user);
      setEmailVerificationNeeded(false);
      setPendingEmail('');
      
      toast.dismiss(loadingToast);
      showSuccessToast(`Bienvenue ${user.full_name}!`, 'Connexion réussie');
      
      return { success: true };
    } catch (error) {
      toast.dismiss(loadingToast);
      
      if (error.response?.status === 403) {
        // Email non vérifié
        setEmailVerificationNeeded(true);
        setPendingEmail(email);
        showErrorToast(error.response?.data?.detail || 'Email non vérifié', 'Vérification requise');
        return { 
          success: false, 
          error: error.response?.data?.detail || 'Email non vérifié',
          needsVerification: true
        };
      }
      
      showErrorToast(error, 'Erreur de connexion');
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur de connexion' 
      };
    }
  };

  const register = async (userData) => {
    const loadingToast = showLoadingToast('Création du compte...');
    
    try {
      const response = await api.post('/api/auth/register', userData);
      setEmailVerificationNeeded(true);
      setPendingEmail(userData.email);
      
      toast.dismiss(loadingToast);
      showSuccessToast(response.data.message, 'Inscription réussie');
      
      return { 
        success: true, 
        message: response.data.message,
        needsVerification: true
      };
    } catch (error) {
      toast.dismiss(loadingToast);
      showErrorToast(error, 'Erreur d\'inscription');
      
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur lors de l\'inscription' 
      };
    }
  };

  const verifyEmail = async (email, code) => {
    const loadingToast = showLoadingToast('Vérification en cours...');
    
    try {
      const response = await api.post('/api/auth/verify-email', { email, code });
      setEmailVerificationNeeded(false);
      setPendingEmail('');
      
      toast.dismiss(loadingToast);
      showSuccessToast(response.data.message, 'Email vérifié');
      
      return { success: true, message: response.data.message };
    } catch (error) {
      toast.dismiss(loadingToast);
      showErrorToast(error, 'Erreur de vérification');
      
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Code invalide ou expiré' 
      };
    }
  };

  const resendVerification = async (email) => {
    const loadingToast = showLoadingToast('Envoi du code...');
    
    try {
      const response = await api.post('/api/auth/resend-verification', { email });
      
      toast.dismiss(loadingToast);
      showSuccessToast(response.data.message, 'Code envoyé');
      
      return { success: true, message: response.data.message };
    } catch (error) {
      toast.dismiss(loadingToast);
      showErrorToast(error, 'Erreur d\'envoi');
      
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur lors de l\'envoi' 
      };
    }
  };

  const forgotPassword = async (email) => {
    const loadingToast = showLoadingToast('Envoi du lien de réinitialisation...');
    
    try {
      const response = await api.post('/api/auth/forgot-password', { email });
      
      toast.dismiss(loadingToast);
      showSuccessToast(response.data.message, 'Email envoyé');
      
      return { success: true, message: response.data.message };
    } catch (error) {
      toast.dismiss(loadingToast);
      showErrorToast(error, 'Erreur d\'envoi');
      
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur lors de l\'envoi' 
      };
    }
  };

  const resetPassword = async (token, newPassword) => {
    const loadingToast = showLoadingToast('Réinitialisation du mot de passe...');
    
    try {
      const response = await api.post('/api/auth/reset-password', { 
        token, 
        new_password: newPassword 
      });
      
      toast.dismiss(loadingToast);
      showSuccessToast(response.data.message, 'Mot de passe réinitialisé');
      
      return { success: true, message: response.data.message };
    } catch (error) {
      toast.dismiss(loadingToast);
      showErrorToast(error, 'Erreur de réinitialisation');
      
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Token invalide ou expiré' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setEmailVerificationNeeded(false);
    setPendingEmail('');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      register, 
      logout, 
      loading,
      verifyEmail,
      resendVerification,
      forgotPassword,
      resetPassword,
      emailVerificationNeeded,
      pendingEmail
    }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Header Component
const Header = () => {
  const { user, logout } = useAuth();

  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-900"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/5 to-purple-500/10"></div>
      
      <div className="relative backdrop-blur-sm border-b border-slate-700/50 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4 sm:py-6">
            
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur opacity-75 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 p-2 sm:p-3 rounded-xl shadow-2xl">
                  <img 
                    src="/brand/cricar-logo.png" 
                    alt="CriCar" 
                    className="h-12 w-12 sm:h-14 sm:w-14 object-cover rounded-lg filter brightness-0 invert drop-shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <Car className="h-12 w-12 sm:h-14 sm:w-14 text-white drop-shadow-lg" style={{display: 'none'}} />
                </div>
              </div>
              
              <div className="hidden sm:block">
                <h1 className="text-xl sm:text-2xl font-bold text-white drop-shadow-lg">CriCar</h1>
                <p className="text-blue-100 text-sm font-medium drop-shadow">Location de véhicules en Algérie</p>
              </div>
              
              <div className="sm:hidden">
                <h1 className="text-lg font-bold text-white drop-shadow-lg">CriCar</h1>
                <p className="text-blue-100 text-xs">Location en Algérie</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <div className="hidden sm:flex items-center gap-3">
                <div className="text-right">
                  <p className="text-white font-semibold text-sm sm:text-base">
                    Bonjour, {user?.full_name || 'Utilisateur'}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full shadow-lg
                      ${user?.role === 'admin' ? 'bg-gradient-to-r from-purple-500 to-indigo-600' :
                        user?.role === 'agency_owner' ? 'bg-gradient-to-r from-blue-500 to-cyan-600' :
                        'bg-gradient-to-r from-green-500 to-emerald-600'}`}>
                      {user?.role === 'admin' ? <Shield className="h-3 w-3 text-white" /> :
                       user?.role === 'agency_owner' ? <Building2 className="h-3 w-3 text-white" /> :
                       <User className="h-3 w-3 text-white" />}
                      <span className="text-white text-xs font-bold">
                        {user?.role === 'admin' ? 'Administrateur' :
                         user?.role === 'agency_owner' ? 'Propriétaire d\'Agence' :
                         'Client'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={logout}
                className="relative group p-2 sm:p-3 bg-slate-700/80 hover:bg-red-600/80 border border-slate-600/50 
                         hover:border-red-500/50 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg 
                         backdrop-blur-sm"
                aria-label="Se déconnecter"
              >
                <LogOut className="h-5 w-5 text-slate-300 group-hover:text-white transition-colors" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Email Verification Modal
const EmailVerificationModal = ({ isOpen, onClose, email }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { verifyEmail, resendVerification } = useAuth();

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Le code doit contenir 6 chiffres');
      return;
    }

    setLoading(true);
    setError('');
    
    const result = await verifyEmail(email, code);
    
    if (result.success) {
      setMessage(result.message);
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 2000);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setLoading(true);
    const result = await resendVerification(email);
    
    if (result.success) {
      setMessage('Nouveau code envoyé ! Vérifiez votre boîte email.');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 
                   ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-300`}>
      <div className="bg-slate-800/95 border border-slate-600/50 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Mail className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Vérification Email</h2>
          <p className="text-blue-100 text-sm">Code envoyé à {email}</p>
        </div>

        {/* Content */}
        <div className="p-8">
          
          {message ? (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-12 w-12 text-emerald-400" />
              </div>
              <div className="space-y-2">
                <h3 className="text-white text-lg font-semibold">Email vérifié !</h3>
                <p className="text-emerald-300 text-sm">{message}</p>
                <p className="text-slate-400 text-xs">Redirection automatique...</p>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-6 bg-red-500/10 border border-red-400/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                    <p className="text-red-300 text-sm">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleVerify} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-white text-sm font-medium text-center block">
                    Code de vérification (6 chiffres)
                  </label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    className="w-full bg-slate-700/80 border border-slate-600/50 text-white rounded-xl px-4 py-4 
                             text-center text-2xl font-mono tracking-widest
                             focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200"
                  />
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    disabled={loading || code.length !== 6}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 
                             text-white font-semibold py-3 rounded-xl shadow-lg transition-all duration-300 
                             hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                             flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        Vérification...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Vérifier mon email
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={loading}
                    className="w-full bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-xl 
                             border border-slate-600 transition-all duration-200 hover:scale-105
                             flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Renvoyer le code
                  </button>
                </div>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={onClose}
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                >
                  Fermer
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Password Reset Modal  
const PasswordResetModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { forgotPassword } = useAuth();

  const handleReset = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError('');
    
    const result = await forgotPassword(email);
    
    if (result.success) {
      setMessage(result.message);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleClose = () => {
    setEmail('');
    setMessage('');
    setError('');
    onClose();
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 
                   ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-300`}>
      <div className="bg-slate-800/95 border border-slate-600/50 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-pink-600 px-8 py-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Key className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Mot de passe oublié ?</h2>
          <p className="text-red-100 text-sm">Pas de problème, nous allons vous aider</p>
        </div>

        {/* Content */}
        <div className="p-8">
          
          {message ? (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                <Mail className="h-12 w-12 text-emerald-400" />
              </div>
              <div className="space-y-2">
                <h3 className="text-white text-lg font-semibold">Email envoyé !</h3>
                <p className="text-emerald-300 text-sm">{message}</p>
                <p className="text-slate-400 text-xs">Vérifiez votre boîte de réception et vos spams</p>
              </div>
              <button
                onClick={handleClose}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
              >
                Compris
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-6 bg-red-500/10 border border-red-400/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                    <p className="text-red-300 text-sm">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleReset} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-white text-sm font-medium">Votre adresse email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre.email@exemple.com"
                    required
                    className="w-full bg-slate-700/80 border border-slate-600/50 text-white rounded-xl px-4 py-3 
                             focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-all duration-200"
                  />
                  <p className="text-slate-400 text-xs">
                    Nous vous enverrons un lien de réinitialisation sécurisé
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    disabled={loading || !email}
                    className="w-full bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 
                             text-white font-semibold py-3 rounded-xl shadow-lg transition-all duration-300 
                             hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                             flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Envoyer le lien
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleClose}
                    className="w-full bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-xl 
                             border border-slate-600 transition-all duration-200 hover:scale-105"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Password Reset Page
const PasswordResetPage = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    setError('');
    
    const result = await resetPassword(token, password);
    
    if (result.success) {
      setMessage('Mot de passe mis à jour avec succès ! Redirection...');
      setTimeout(() => navigate('/'), 3000);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  if (!token) return null;

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-red-900 to-pink-900"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-red-900/30 via-transparent to-pink-800/20"></div>
      
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-pink-500 rounded-3xl blur-xl opacity-60 animate-pulse"></div>
              <div className="relative bg-gradient-to-r from-red-600 to-pink-600 p-6 rounded-3xl shadow-2xl">
                <Key className="h-16 w-16 text-white drop-shadow-lg" />
              </div>
            </div>
            
            <h1 className="text-4xl font-bold text-white mt-6 mb-2 drop-shadow-lg">Nouveau mot de passe</h1>
            <p className="text-red-200 text-lg font-medium">Définissez un mot de passe sécurisé</p>
          </div>

          <div className="bg-slate-800/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl shadow-2xl overflow-hidden">
            
            <div className="bg-gradient-to-r from-red-600 to-pink-600 px-8 py-6 text-center">
              <h2 className="text-xl font-bold text-white">Réinitialisation</h2>
              <p className="text-red-100 text-sm">Choisissez un mot de passe fort</p>
            </div>

            <div className="p-8">
              
              {message ? (
                <div className="text-center space-y-4">
                  <CheckCircle className="h-16 w-16 text-emerald-400 mx-auto" />
                  <h3 className="text-white text-lg font-semibold">Succès !</h3>
                  <p className="text-emerald-300">{message}</p>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="mb-6 bg-red-500/10 border border-red-400/30 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                        <p className="text-red-300 text-sm">{error}</p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-white text-sm font-medium">Nouveau mot de passe</label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          required
                          minLength={6}
                          className="w-full bg-slate-700/80 border border-slate-600/50 text-white rounded-xl px-4 py-3 pr-14
                                   focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-all duration-200"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 flex items-center justify-center w-12 text-slate-400 
                                   hover:text-white transition-colors duration-200 rounded-r-xl hover:bg-slate-600/30"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-white text-sm font-medium">Confirmer le mot de passe</label>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        minLength={6}
                        className="w-full bg-slate-700/80 border border-slate-600/50 text-white rounded-xl px-4 py-3 
                                 focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-all duration-200"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 
                               text-white font-semibold py-4 rounded-xl shadow-xl transition-all duration-300 
                               hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                               flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                          Mise à jour...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-5 w-5" />
                          Mettre à jour mon mot de passe
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    full_name: '',
    phone: '',
    role: 'client'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const { login, register, emailVerificationNeeded, pendingEmail } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (isLogin) {
      const result = await login(formData.email, formData.password);
      if (!result.success) {
        setError(result.error);
        if (result.needsVerification) {
          // L'email verification modal sera gérée par l'état global
        }
      }
    } else {
      const result = await register(formData);
      if (result.success) {
        setError('');
        // Le modal de vérification email s'ouvrira automatiquement
      } else {
        setError(result.error);
      }
    }
    setLoading(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/30 via-transparent to-purple-800/20"></div>
      
      <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur-xl opacity-60 animate-pulse"></div>
              
              <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-3xl shadow-2xl">
                <img 
                  src="/brand/cricar-logo.png" 
                  alt="CriCar" 
                  className="h-16 w-16 object-cover filter brightness-0 invert drop-shadow-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <Car className="h-16 w-16 text-white drop-shadow-lg" style={{display: 'none'}} />
              </div>
            </div>
            
            <h1 className="text-4xl font-bold text-white mt-6 mb-2 drop-shadow-lg">CriCar</h1>
            <p className="text-blue-200 text-lg font-medium">Location de véhicules en Algérie</p>
          </div>

          <div className="bg-slate-800/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl shadow-2xl overflow-hidden">
            
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6 text-center">
              <h2 className="text-2xl font-bold text-white mb-2">
                {isLogin ? 'Connexion' : 'Créer un compte'}
              </h2>
              <p className="text-indigo-100 text-sm">
                {isLogin ? 'Accédez à votre espace personnel' : 'Rejoignez la communauté CriCar'}
              </p>
            </div>

            <div className="p-8">
              
              {error && (
                <div className="mb-6 bg-red-500/10 border border-red-400/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                    <p className="text-red-300 font-medium text-sm">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                
                {!isLogin && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-white text-sm font-medium flex items-center gap-2">
                          <User className="h-4 w-4 text-indigo-400" />
                          Nom d'utilisateur
                        </label>
                        <input
                          type="text"
                          value={formData.username}
                          onChange={(e) => handleChange('username', e.target.value)}
                          required
                          className="w-full bg-slate-700/80 border border-slate-600/50 text-white rounded-xl px-4 py-3 
                                   focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-400/50
                                   transition-all duration-200 placeholder-slate-400"
                          placeholder="votre_nom"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-white text-sm font-medium flex items-center gap-2">
                          <User className="h-4 w-4 text-indigo-400" />
                          Nom complet
                        </label>
                        <input
                          type="text"
                          value={formData.full_name}
                          onChange={(e) => handleChange('full_name', e.target.value)}
                          required
                          className="w-full bg-slate-700/80 border border-slate-600/50 text-white rounded-xl px-4 py-3 
                                   focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-400/50
                                   transition-all duration-200 placeholder-slate-400"
                          placeholder="Votre nom complet"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-white text-sm font-medium flex items-center gap-2">
                        <Phone className="h-4 w-4 text-indigo-400" />
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        className="w-full bg-slate-700/80 border border-slate-600/50 text-white rounded-xl px-4 py-3 
                                 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-400/50
                                 transition-all duration-200 placeholder-slate-400"
                        placeholder="+213 XXX XXX XXX"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-white text-sm font-medium flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-indigo-400" />
                        Type de compte
                      </label>
                      <select 
                        value={formData.role} 
                        onChange={(e) => handleChange('role', e.target.value)}
                        className="w-full bg-slate-700/80 border border-slate-600/50 text-white rounded-xl px-4 py-3 
                                 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-400/50
                                 transition-all duration-200"
                        required
                      >
                        <option value="">Choisir un type</option>
                        <option value="client">🚗 Client</option>
                        <option value="agency_owner">🏢 Propriétaire d'Agence</option>
                      </select>
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="text-white text-sm font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4 text-indigo-400" />
                    Adresse email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="w-full bg-slate-700/80 border border-slate-600/50 text-white rounded-xl px-4 py-3 
                             focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-400/50
                             transition-all duration-200 placeholder-slate-400"
                    placeholder="votre.email@exemple.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-white text-sm font-medium flex items-center gap-2">
                    <Key className="h-4 w-4 text-indigo-400" />
                    Mot de passe
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      required
                      className="w-full bg-slate-700/80 border border-slate-600/50 text-white rounded-xl px-4 py-3 pr-14
                               focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-400/50
                               transition-all duration-200 placeholder-slate-400"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center justify-center w-12 text-slate-400 
                               hover:text-white transition-colors duration-200 rounded-r-xl hover:bg-slate-600/30"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                
                <button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 
                           text-white font-semibold py-4 rounded-xl shadow-xl transition-all duration-300 
                           hover:scale-105 hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed
                           disabled:hover:scale-100"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-3">
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                      <span>{isLogin ? 'Connexion...' : 'Inscription...'}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      {isLogin ? (
                        <>
                          <LogOut className="h-5 w-5" />
                          Se connecter
                        </>
                      ) : (
                        <>
                          <User className="h-5 w-5" />
                          Créer mon compte
                        </>
                      )}
                    </div>
                  )}
                </button>
                
                {isLogin && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowPasswordReset(true)}
                      className="text-indigo-300 hover:text-indigo-200 text-sm font-medium transition-colors duration-200 
                               hover:underline"
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                )}
              </form>
              
              <div className="mt-8 pt-6 border-t border-slate-700/50 text-center">
                <button
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                    setFormData({
                      email: '',
                      password: '',
                      username: '',
                      full_name: '',
                      phone: '',
                      role: 'client'
                    });
                  }}
                  className="text-blue-300 hover:text-blue-200 font-medium transition-all duration-200 
                           hover:underline text-base"
                >
                  {isLogin ? (
                    <span className="flex items-center justify-center gap-2">
                      <Plus className="h-4 w-4" />
                      Pas de compte ? Créer un compte
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <LogOut className="h-4 w-4" />
                      Déjà un compte ? Se connecter
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle className="h-5 w-5" />
                  <span className="text-sm font-medium">Sécurisé</span>
                </div>
                <div className="flex items-center gap-2 text-blue-400">
                  <Shield className="h-5 w-5" />
                  <span className="text-sm font-medium">Crypté</span>
                </div>
                <div className="flex items-center gap-2 text-purple-400">
                  <Star className="h-5 w-5" />
                  <span className="text-sm font-medium">Fiable</span>
                </div>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">
                Plateforme de location de véhicules de confiance en Algérie. 
                Vos données sont protégées et sécurisées.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Email Verification Modal */}
      <EmailVerificationModal 
        isOpen={emailVerificationNeeded}
        onClose={() => {/* Géré par l'AuthProvider */}}
        email={pendingEmail}
      />

      {/* Password Reset Modal */}
      <PasswordResetModal 
        isOpen={showPasswordReset}
        onClose={() => setShowPasswordReset(false)}
      />
    </div>
  );
};

// Simple Admin Dashboard
const AdminDashboard = () => {
  const [stats, setStats] = useState({});
  const [agencies, setAgencies] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [showAgencyDetails, setShowAgencyDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [statsRes, agenciesRes, clientsRes] = await Promise.all([
        api.get('/api/admin/stats').catch(() => ({ data: {} })),
        api.get('/api/admin/agencies').catch(() => ({ data: { agencies: [] } })),
        api.get('/api/admin/clients').catch(() => ({ data: { clients: [] } }))
      ]);
      
      setStats(statsRes.data || {});
      setAgencies(agenciesRes.data.agencies || []);
      setClients(clientsRes.data.clients || []);
      
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateAgencyStatus = async (agencyId, status) => {
    try {
      setActionLoading(prev => ({ ...prev, [agencyId]: true }));
      
      const response = await api.put(`/api/admin/agencies/${agencyId}/status`, null, {
        params: { status }
      });
      
      await fetchDashboardData();
      
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erreur lors de la mise à jour' };
    } finally {
      setActionLoading(prev => ({ ...prev, [agencyId]: false }));
    }
  };

  const deleteAgency = async (agencyId) => {
    try {
      const response = await api.delete(`/api/admin/agencies/${agencyId}`);
      await fetchDashboardData();
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erreur lors de la suppression' };
    }
  };

  const updateClientStatus = async (clientId, isActive) => {
    try {
      setActionLoading(prev => ({ ...prev, [clientId]: true }));
      
      const response = await api.put(`/api/admin/clients/${clientId}/status`, null, {
        params: { is_active: isActive }
      });
      
      await fetchDashboardData();
      
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erreur lors de la mise à jour' };
    } finally {
      setActionLoading(prev => ({ ...prev, [clientId]: false }));
    }
  };

  const deleteClient = async (clientId) => {
    try {
      const response = await api.delete(`/api/admin/clients/${clientId}`);
      await fetchDashboardData();
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erreur lors de la suppression' };
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, color = "blue", subtitle }) => (
    <div className={`bg-gradient-to-br from-${color}-500/20 to-${color}-600/10 
                    border border-${color}-400/30 rounded-2xl p-4 sm:p-6 
                    hover:shadow-xl transition-all duration-300 hover:scale-105`}>
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <div className={`p-2 sm:p-3 bg-${color}-500/20 rounded-xl`}>
          <Icon className={`h-5 w-5 sm:h-6 sm:w-6 text-${color}-400`} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full 
                         ${trend > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
            <TrendingUp className="h-3 w-3" />
            {trend > 0 ? '+' : ''}{trend}%
          </div>
        )}
      </div>
      <div className="space-y-1">
        <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{value}</p>
        <p className="text-slate-300 text-sm font-medium">{title}</p>
        {subtitle && <p className="text-slate-400 text-xs">{subtitle}</p>}
      </div>
    </div>
  );

  const AgencyCard = ({ agency }) => (
    <div className="bg-gradient-to-r from-slate-800/90 to-slate-700/90 backdrop-blur-sm 
                    border border-slate-600/50 rounded-2xl p-4 sm:p-6 
                    hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-3">
            <h3 className="text-white font-semibold text-lg">{agency.name}</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-medium border
              ${agency.status === 'active' 
                ? 'bg-emerald-500/20 text-emerald-400 border-emerald-400/30' 
                : agency.status === 'pending'
                ? 'bg-amber-500/20 text-amber-400 border-amber-400/30'
                : 'bg-red-500/20 text-red-400 border-red-400/30'
              }`}>
              {agency.status === 'active' ? '✅ Active' : 
               agency.status === 'pending' ? '⏳ En attente' : '❌ Rejetée'}
            </span>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="flex items-center gap-2 text-slate-300">
              <Mail className="h-4 w-4 text-blue-400" />
              <span className="truncate">{agency.email}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <MapPin className="h-4 w-4 text-green-400" />
              <span>{agency.city}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <Car className="h-4 w-4 text-purple-400" />
              <span>{agency.vehicle_count} véhicules</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <span>Inscrit le {new Date(agency.created_at).toLocaleDateString()}</span>
            <span>•</span>
            <span className="font-medium text-blue-300">{agency.subscription_amount.toLocaleString()} DZD/mois</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          {agency.status === 'pending' && (
            <>
              <button
                onClick={async () => {
                  const result = await updateAgencyStatus(agency.id, 'active');
                  if (!result.success) window.alert(result.error);
                }}
                disabled={actionLoading[agency.id]}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg 
                         transition-colors flex items-center gap-2 disabled:opacity-50 text-sm font-medium"
              >
                {actionLoading[agency.id] ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Approuver</span>
              </button>
              <button
                onClick={async () => {
                  const result = await updateAgencyStatus(agency.id, 'rejected');
                  if (!result.success) window.alert(result.error);
                }}
                disabled={actionLoading[agency.id]}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg 
                         transition-colors flex items-center gap-2 disabled:opacity-50 text-sm font-medium"
              >
                <XCircle className="h-4 w-4" />
                <span className="hidden sm:inline">Rejeter</span>
              </button>
            </>
          )}
          
          <button
            onClick={() => {
              setSelectedAgency(agency);
              setShowAgencyDetails(true);
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg 
                     transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <Eye className="h-4 w-4" />
            <span className="hidden sm:inline">Détails</span>
          </button>
          
          <button
            onClick={async () => {
              if (window.confirm(`Supprimer définitivement l'agence ${agency.name} ?`)) {
                const result = await deleteAgency(agency.id);
                if (!result.success) window.alert(result.error);
              }
            }}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg 
                     transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">Supprimer</span>
          </button>
        </div>
      </div>
    </div>
  );

  const ClientCard = ({ client }) => (
    <div className="bg-gradient-to-r from-slate-800/90 to-slate-700/90 backdrop-blur-sm 
                    border border-slate-600/50 rounded-2xl p-4 sm:p-6 
                    hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-r from-emerald-500 to-green-600 
                           flex items-center justify-center shadow-lg">
              <span className="text-white text-lg sm:text-xl font-bold">
                {client.name.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            {client.isVip && (
              <div className="absolute -top-1 -right-1 bg-gradient-to-r from-yellow-500 to-amber-500 
                           rounded-full p-1 shadow-lg">
                <Star className="h-3 w-3 text-white fill-current" />
              </div>
            )}
            <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-slate-800
              ${client.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
          </div>
          
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="text-white font-semibold text-lg">{client.name}</h3>
              {client.isVip && (
                <span className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-xs 
                               font-bold px-2 py-1 rounded-full shadow-lg">
                  VIP
                </span>
              )}
              <span className={`px-2 py-1 rounded-full text-xs font-medium border
                ${client.status === 'active' 
                  ? 'bg-emerald-500/20 text-emerald-400 border-emerald-400/30' 
                  : 'bg-red-500/20 text-red-400 border-red-400/30'
                }`}>
                {client.status === 'active' ? '✅ Actif' : '❌ Inactif'}
              </span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="flex items-center gap-2 text-slate-300">
                <Mail className="h-4 w-4 text-blue-400" />
                <span className="truncate">{client.email}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <MapPin className="h-4 w-4 text-green-400" />
                <span>{client.city}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Calendar className="h-4 w-4 text-purple-400" />
                <span>{client.bookings} réservations</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-xs text-slate-400">
              <span>Membre depuis {new Date(client.joined).toLocaleDateString()}</span>
              <span>•</span>
              <span className="font-medium text-emerald-300">{client.spent.toLocaleString()} DZD dépensés</span>
              <span>•</span>
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 text-amber-400 fill-current" />
                <span className="text-amber-300">{client.rating}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg 
                     transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Message</span>
          </button>
          
          {client.status === 'active' ? (
            <button
              onClick={async () => {
                const result = await updateClientStatus(client.id, false);
                if (!result.success) window.alert(result.error);
              }}
              disabled={actionLoading[client.id]}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg 
                       transition-colors flex items-center gap-2 disabled:opacity-50 text-sm font-medium"
            >
              {actionLoading[client.id] ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Ban className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Suspendre</span>
            </button>
          ) : (
            <button
              onClick={async () => {
                const result = await updateClientStatus(client.id, true);
                if (!result.success) window.alert(result.error);
              }}
              disabled={actionLoading[client.id]}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg 
                       transition-colors flex items-center gap-2 disabled:opacity-50 text-sm font-medium"
            >
              {actionLoading[client.id] ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Activer</span>
            </button>
          )}
          
          <button
            onClick={async () => {
              if (window.confirm(`Supprimer définitivement le client ${client.name} ?`)) {
                const result = await deleteClient(client.id);
                if (!result.success) window.alert(result.error);
              }
            }}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg 
                     transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">Supprimer</span>
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin h-16 w-16 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-white text-xl">Chargement de l'administration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900">
      <div className="bg-gradient-to-r from-slate-800/95 to-indigo-800/95 backdrop-blur-sm 
                    border-b border-slate-700/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6 space-y-4 sm:space-y-6">
            
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 sm:gap-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-2xl blur opacity-75"></div>
                  <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 p-3 sm:p-4 rounded-2xl">
                    <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                </div>
                
                <div>
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">Administration CriCar</h1>
                  <p className="text-purple-200 text-sm">Gestion des agences et clients</p>
                </div>
              </div>

              <button
                onClick={fetchDashboardData}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl 
                         font-medium transition-all duration-200 hover:scale-105 shadow-lg
                         flex items-center gap-2 text-sm sm:text-base"
              >
                <RefreshCw className="h-4 w-4" />
                <span className="hidden sm:inline">Actualiser</span>
              </button>
            </div>

            <div className="flex space-x-1 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              {[
                { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
                { id: 'agencies', label: 'Agences', icon: Building2 },
                { id: 'clients', label: 'Clients', icon: Users },
                { id: 'analytics', label: 'Analytics', icon: TrendingUp },
                { id: 'settings', label: 'Paramètres', icon: Settings }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 rounded-xl font-medium transition-all duration-200 
                            whitespace-nowrap flex-shrink-0
                    ${activeView === tab.id 
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg' 
                      : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                    }`}
                >
                  <tab.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        
        {activeView === 'overview' && (
          <div className="space-y-6 sm:space-y-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <StatCard
                title="Agences totales"
                value={stats.total_agencies || 0}
                icon={Building2}
                color="blue"
                trend={12}
                subtitle="Toutes confondues"
              />
              <StatCard
                title="Agences actives"
                value={stats.active_agencies || 0}
                icon={CheckCircle}
                color="emerald"
                trend={8}
                subtitle="Opérationnelles"
              />
              <StatCard
                title="En attente"
                value={stats.pending_approvals || 0}
                icon={Clock}
                color="amber"
                subtitle="Approbations"
              />
              <StatCard
                title="Revenus (mois)"
                value={`${Math.round((stats.monthly_revenue || 0) / 1000)}K DZD`}
                icon={DollarSign}
                color="purple"
                trend={15}
                subtitle="Ce mois-ci"
              />
            </div>

            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <StatCard
                title="Véhicules"
                value={stats.total_vehicles || 0}
                icon={Car}
                color="indigo"
                subtitle="Dans la flotte"
              />
              <StatCard
                title="Clients"
                value={stats.total_clients || 0}
                icon={Users}
                color="cyan"
                trend={23}
                subtitle="Inscrits"
              />
              <StatCard
                title="Réservations"
                value={stats.total_reservations || 0}
                icon={Calendar}
                color="emerald"
                subtitle="Total"
              />
              <StatCard
                title="Réservations actives"
                value={stats.active_reservations || 0}
                icon={Activity}
                color="amber"
                subtitle="En cours"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <button
                onClick={() => setActiveView('agencies')}
                className="bg-gradient-to-br from-blue-600/20 to-indigo-600/10 border border-blue-500/30 
                         rounded-2xl p-4 sm:p-6 text-left hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-4 mb-3">
                  <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
                  <div>
                    <h3 className="text-white font-semibold text-lg">Gérer les agences</h3>
                    <p className="text-blue-200 text-sm">{stats.pending_approvals || 0} en attente</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm">Approuver, rejeter ou gérer les agences partenaires</p>
              </button>

              <button
                onClick={() => setActiveView('clients')}
                className="bg-gradient-to-br from-emerald-600/20 to-green-600/10 border border-emerald-500/30 
                         rounded-2xl p-4 sm:p-6 text-left hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-4 mb-3">
                  <Users className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-400" />
                  <div>
                    <h3 className="text-white font-semibold text-lg">Gestion clients</h3>
                    <p className="text-emerald-200 text-sm">{stats.total_clients || 0} inscrits</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm">Gérer les comptes clients et leurs activités</p>
              </button>

              <button
                onClick={() => setActiveView('analytics')}
                className="bg-gradient-to-br from-purple-600/20 to-pink-600/10 border border-purple-500/30 
                         rounded-2xl p-4 sm:p-6 text-left hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center gap-4 mb-3">
                  <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-purple-400" />
                  <div>
                    <h3 className="text-white font-semibold text-lg">Analytics</h3>
                    <p className="text-purple-200 text-sm">+15% ce mois</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm">Statistiques détaillées et rapports de performance</p>
              </button>
            </div>
          </div>
        )}

        {activeView === 'agencies' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white">Gestion des Agences</h2>
                <p className="text-slate-300">Approuver et gérer les agences partenaires</p>
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={fetchDashboardData}
                  className="bg-slate-700/50 border border-slate-600 text-white hover:bg-slate-600 px-4 py-2 rounded-xl
                           flex items-center gap-2 text-sm font-medium transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Actualiser
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {agencies.length === 0 ? (
                <div className="text-center py-12 bg-slate-800/50 rounded-2xl border border-slate-700/50">
                  <Building2 className="h-16 w-16 text-slate-500 mx-auto mb-4" />
                  <h3 className="text-white text-lg font-semibold mb-2">Aucune agence</h3>
                  <p className="text-slate-400">Les nouvelles demandes d'agence apparaîtront ici</p>
                </div>
              ) : (
                agencies.map(agency => (
                  <AgencyCard key={agency.id} agency={agency} />
                ))
              )}
            </div>
          </div>
        )}

        {activeView === 'clients' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white">Gestion des Clients</h2>
                <p className="text-slate-300">Administration des comptes clients</p>
              </div>
              
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rechercher un client..."
                  className="bg-slate-800/80 border border-slate-600/50 text-white rounded-xl px-4 py-3 pl-10 
                           w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all duration-200"
                />
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
              <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/10 border border-blue-400/30 rounded-2xl p-4 text-center">
                <Users className="h-6 w-6 text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{stats.total_clients || 0}</p>
                <p className="text-blue-300 text-sm font-medium">Total clients</p>
              </div>
              
              <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/10 border border-emerald-400/30 rounded-2xl p-4 text-center">
                <CheckCircle className="h-6 w-6 text-emerald-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{clients.filter(c => c.status === 'active').length}</p>
                <p className="text-emerald-300 text-sm font-medium">Actifs</p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/10 border border-purple-400/30 rounded-2xl p-4 text-center">
                <Star className="h-6 w-6 text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{clients.filter(c => c.isVip).length}</p>
                <p className="text-purple-300 text-sm font-medium">VIP</p>
              </div>
              
              <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/10 border border-amber-400/30 rounded-2xl p-4 text-center">
                <Calendar className="h-6 w-6 text-amber-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{clients.reduce((sum, c) => sum + c.bookings, 0)}</p>
                <p className="text-amber-300 text-sm font-medium">Réservations</p>
              </div>
              
              <div className="bg-gradient-to-br from-red-500/20 to-rose-500/10 border border-red-400/30 rounded-2xl p-4 text-center">
                <XCircle className="h-6 w-6 text-red-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{clients.filter(c => c.status === 'inactive').length}</p>
                <p className="text-red-300 text-sm font-medium">Inactifs</p>
              </div>
            </div>

            <div className="space-y-4">
              {clients.map(client => (
                <ClientCard key={client.id} client={client} />
              ))}
            </div>
          </div>
        )}

        {!['overview', 'agencies', 'clients'].includes(activeView) && (
          <div className="text-center py-20">
            <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-12 max-w-md mx-auto">
              <Settings className="h-16 w-16 text-slate-500 mx-auto mb-4" />
              <h3 className="text-white text-xl font-semibold mb-2">
                {activeView === 'analytics' && 'Analytics'}
                {activeView === 'settings' && 'Paramètres'}
              </h3>
              <p className="text-slate-400">Cette section sera bientôt disponible</p>
            </div>
          </div>
        )}
      </div>

      {showAgencyDetails && selectedAgency && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-slate-600/50 
                        shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700/50">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Détails de l'agence</h3>
                <button
                  onClick={() => setShowAgencyDetails(false)}
                  className="text-slate-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-slate-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-white font-semibold mb-3">Informations générales</h4>
                  <div className="space-y-3 text-sm">
                    <div><span className="text-slate-400">Nom:</span> <span className="text-white font-medium">{selectedAgency.name}</span></div>
                    <div><span className="text-slate-400">Email:</span> <span className="text-white font-medium">{selectedAgency.email}</span></div>
                    <div><span className="text-slate-400">Ville:</span> <span className="text-white font-medium">{selectedAgency.city}</span></div>
                    <div><span className="text-slate-400">Véhicules:</span> <span className="text-white font-medium">{selectedAgency.vehicle_count}</span></div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-white font-semibold mb-3">Statut et performance</h4>
                  <div className="space-y-3 text-sm">
                    <div><span className="text-slate-400">Statut:</span> 
                      <span className={`ml-2 px-2 py-1 rounded text-xs font-medium
                        ${selectedAgency.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 
                          selectedAgency.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-red-500/20 text-red-400'}`}>
                        {selectedAgency.status === 'active' ? 'Active' : 
                         selectedAgency.status === 'pending' ? 'En attente' : 'Rejetée'}
                      </span>
                    </div>
                    <div><span className="text-slate-400">Inscription:</span> <span className="text-white font-medium">{new Date(selectedAgency.created_at).toLocaleDateString()}</span></div>
                    <div><span className="text-slate-400">Abonnement:</span> <span className="text-white font-medium">{selectedAgency.subscription_amount.toLocaleString()} DZD/mois</span></div>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4 border-t border-slate-700/50">
                <button
                  onClick={() => setShowAgencyDetails(false)}
                  className="flex-1 bg-slate-700/50 border border-slate-600 text-white hover:bg-slate-600 py-2 px-4 rounded-xl transition-colors"
                >
                  Fermer
                </button>
                <button className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-xl transition-colors flex items-center justify-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Contacter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Agency Dashboard - Using Enhanced Component
const AgencyDashboard = () => {
  return <EnhancedAgencyDashboard api={api} />;
};

// Modern Client Dashboard - Fully Functional
const ClientDashboard = () => {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState('discover');
  const [vehicles, setVehicles] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    city: 'all',
    category: 'all',
    priceRange: 'all'
  });
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showReservationModal, setShowReservationModal] = useState(false);

  useEffect(() => {
    fetchClientData();
  }, []);

  useEffect(() => {
    filterVehicles();
  }, [vehicles, searchQuery, filters]);

  const fetchClientData = async () => {
    try {
      setLoading(true);
      
      const [vehiclesRes, statsRes, reservationsRes] = await Promise.all([
        api.get('/api/vehicles'),
        api.get('/api/user/stats').catch(() => ({ data: {} })),
        api.get('/api/reservations/my').catch(() => ({ data: { reservations: [] } }))
      ]);
      
      setVehicles(vehiclesRes.data.vehicles || []);
      setUserStats(statsRes.data || {});
      setReservations(reservationsRes.data.reservations || []);
      
    } catch (error) {
      console.error('Error fetching client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterVehicles = () => {
    let filtered = vehicles;

    if (searchQuery) {
      filtered = filtered.filter(vehicle => 
        vehicle.make.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.agency.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.city.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filters.city !== 'all') {
      filtered = filtered.filter(vehicle => vehicle.city === filters.city);
    }

    if (filters.category !== 'all') {
      filtered = filtered.filter(vehicle => vehicle.category === filters.category);
    }

    if (filters.priceRange !== 'all') {
      const [min, max] = filters.priceRange.split('-').map(Number);
      filtered = filtered.filter(vehicle => 
        vehicle.daily_rate >= min && vehicle.daily_rate <= max
      );
    }

    setFilteredVehicles(filtered);
  };

  const createReservation = async (reservationData) => {
    try {
      const response = await api.post('/api/reservations', reservationData);
      await fetchClientData();
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erreur lors de la réservation' };
    }
  };

  const ClientStatCard = ({ title, value, icon: Icon, color = "blue", subtitle, onClick }) => (
    <div 
      onClick={onClick}
      className={`bg-gradient-to-br from-${color}-500/20 to-${color}-600/10 
                  border border-${color}-400/30 rounded-2xl p-4 sm:p-6 
                  hover:shadow-xl transition-all duration-300 hover:scale-105 
                  ${onClick ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 sm:p-3 bg-${color}-500/20 rounded-xl`}>
          <Icon className={`h-5 w-5 sm:h-6 sm:w-6 text-${color}-400`} />
        </div>
      </div>
      <div className="space-y-1">
        <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{value}</p>
        <p className="text-slate-300 text-sm font-medium">{title}</p>
        {subtitle && <p className="text-slate-400 text-xs">{subtitle}</p>}
      </div>
    </div>
  );

  const VehicleCard = ({ vehicle, onReserve }) => (
    <div className="bg-gradient-to-r from-slate-800/90 to-slate-700/90 backdrop-blur-sm 
                    border border-slate-600/50 rounded-2xl overflow-hidden
                    hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
      
      <div className="relative h-48 bg-gradient-to-br from-slate-700 to-slate-800 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10"></div>
        <div className="w-full h-full flex items-center justify-center text-slate-500">
          <Car className="h-16 w-16" />
        </div>
        
        <div className="absolute top-4 right-4 bg-slate-900/90 backdrop-blur-sm px-3 py-1 rounded-full 
                      border border-slate-600/50 z-20">
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 text-amber-400 fill-current" />
            <span className="text-white text-sm font-medium">{vehicle.rating}</span>
            <span className="text-slate-400 text-xs">({vehicle.reviews_count})</span>
          </div>
        </div>

        <div className="absolute bottom-4 left-4 bg-emerald-600/90 backdrop-blur-sm px-3 py-2 rounded-xl z-20">
          <p className="text-white font-bold text-lg">{vehicle.daily_rate.toLocaleString()}</p>
          <p className="text-emerald-100 text-xs">DZD/jour</p>
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-white font-bold text-lg">{vehicle.make} {vehicle.model}</h3>
            <span className="bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded-full text-xs font-medium">
              {vehicle.category}
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-300">
            <div className="flex items-center gap-1">
              <MapPin className="h-4 w-4 text-purple-400" />
              <span>{vehicle.city}</span>
            </div>
            <div className="flex items-center gap-1">
              <Building2 className="h-4 w-4 text-blue-400" />
              <span className="truncate">{vehicle.agency}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {vehicle.features.slice(0, 3).map((feature, index) => (
            <span key={index} className="bg-slate-700/50 text-slate-300 px-2 py-1 rounded-lg text-xs">
              {feature}
            </span>
          ))}
          {vehicle.features.length > 3 && (
            <span className="bg-slate-700/50 text-slate-400 px-2 py-1 rounded-lg text-xs">
              +{vehicle.features.length - 3}
            </span>
          )}
        </div>

        <button
          onClick={() => onReserve(vehicle)}
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 
                   text-white font-semibold py-3 rounded-xl shadow-lg transition-all duration-300 
                   hover:scale-105 flex items-center justify-center gap-2"
        >
          <Calendar className="h-4 w-4" />
          Réserver maintenant
        </button>
      </div>
    </div>
  );

  const ReservationCard = ({ reservation }) => (
    <div className="bg-gradient-to-r from-slate-800/90 to-slate-700/90 backdrop-blur-sm 
                    border border-slate-600/50 rounded-xl p-4 
                    hover:shadow-lg transition-all duration-300">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h4 className="text-white font-medium">{reservation.vehicle_id}</h4>
            <span className={`px-2 py-1 rounded-full text-xs font-medium
              ${reservation.status === 'confirmed' 
                ? 'bg-emerald-500/20 text-emerald-400' 
                : reservation.status === 'pending'
                ? 'bg-amber-500/20 text-amber-400'
                : 'bg-blue-500/20 text-blue-400'
              }`}>
              {reservation.status === 'confirmed' ? '✅ Confirmée' : 
               reservation.status === 'pending' ? '⏳ En attente' : '🚗 Active'}
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-300">
            <span>{new Date(reservation.pickup_date).toLocaleDateString()} - {new Date(reservation.return_date).toLocaleDateString()}</span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-emerald-400 font-bold">{reservation.total_amount?.toLocaleString()} DZD</p>
        </div>
      </div>
    </div>
  );

  const ReservationModal = ({ vehicle, isOpen, onClose, onConfirm }) => {
    const [formData, setFormData] = useState({
      pickup_date: '',
      return_date: '',
      pickup_location: '',
      return_location: '',
      customer_notes: ''
    });
    const [loading, setLoading] = useState(false);

    const calculateTotal = () => {
      if (!formData.pickup_date || !formData.return_date) return 0;
      const pickup = new Date(formData.pickup_date);
      const returnDate = new Date(formData.return_date);
      const days = Math.ceil((returnDate - pickup) / (1000 * 60 * 60 * 24));
      return days > 0 ? days * vehicle.daily_rate : 0;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      
      const reservationData = {
        vehicle_id: vehicle.id,
        pickup_date: new Date(formData.pickup_date).toISOString(),
        return_date: new Date(formData.return_date).toISOString(),
        pickup_location: formData.pickup_location,
        return_location: formData.return_location,
        customer_notes: formData.customer_notes
      };

      const result = await createReservation(reservationData);
      
      if (result.success) {
        onConfirm();
        onClose();
      } else {
        window.alert(result.error);
      }
      setLoading(false);
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div className="bg-slate-800/95 border border-slate-600/50 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Réserver {vehicle.make} {vehicle.model}</h3>
              <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-white text-sm font-medium">Date de prise</label>
                <input
                  type="datetime-local"
                  value={formData.pickup_date}
                  onChange={(e) => setFormData(prev => ({...prev, pickup_date: e.target.value}))}
                  min={new Date().toISOString().slice(0, 16)}
                  required
                  className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-white text-sm font-medium">Date de retour</label>
                <input
                  type="datetime-local"
                  value={formData.return_date}
                  onChange={(e) => setFormData(prev => ({...prev, return_date: e.target.value}))}
                  min={formData.pickup_date || new Date().toISOString().slice(0, 16)}
                  required
                  className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-white text-sm font-medium">Lieu de prise</label>
                <input
                  type="text"
                  value={formData.pickup_location}
                  onChange={(e) => setFormData(prev => ({...prev, pickup_location: e.target.value}))}
                  placeholder="Adresse de prise en charge"
                  required
                  className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-white text-sm font-medium">Lieu de retour</label>
                <input
                  type="text"
                  value={formData.return_location}
                  onChange={(e) => setFormData(prev => ({...prev, return_location: e.target.value}))}
                  placeholder="Adresse de retour"
                  required
                  className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-white text-sm font-medium">Notes (optionnel)</label>
              <textarea
                value={formData.customer_notes}
                onChange={(e) => setFormData(prev => ({...prev, customer_notes: e.target.value}))}
                placeholder="Demandes spéciales..."
                rows={3}
                className="w-full bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3 resize-none"
              />
            </div>

            {calculateTotal() > 0 && (
              <div className="bg-emerald-500/10 border border-emerald-400/20 rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <span className="text-emerald-300 font-medium">Total</span>
                  <span className="text-white font-bold text-xl">{calculateTotal().toLocaleString()} DZD</span>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-xl font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading || calculateTotal() <= 0}
                className="flex-1 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 
                         text-white font-semibold py-3 rounded-xl transition-all duration-200 hover:scale-105 
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Réservation...' : 'Confirmer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin h-16 w-16 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-white text-xl">Chargement de votre espace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Bonjour, {user?.full_name || 'Client'} !
          </h1>
          <p className="text-slate-300 text-lg sm:text-xl">Découvrez nos véhicules disponibles</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <ClientStatCard
            title="Véhicules disponibles"
            value={vehicles.length}
            icon={Car}
            color="blue"
            subtitle="Toutes catégories"
            onClick={() => setActiveView('discover')}
          />
          <ClientStatCard
            title="Mes réservations"
            value={userStats.total_reservations || 0}
            icon={Calendar}
            color="purple"
            subtitle="Total effectuées"
            onClick={() => setActiveView('history')}
          />
          <ClientStatCard
            title="Dépenses totales"
            value={`${Math.round((userStats.total_spent || 0) / 1000)}K DZD`}
            icon={DollarSign}
            color="emerald"
            subtitle="Depuis inscription"
          />
          <ClientStatCard
            title="Membre depuis"
            value={`${userStats.days_member || 0}j`}
            icon={Star}
            color="amber"
            subtitle="Jours d'ancienneté"
          />
        </div>

        <div className="bg-gradient-to-r from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-2xl p-6 sm:p-8 mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 text-center">Trouvez votre véhicule idéal</h2>
          
          <div className="max-w-2xl mx-auto mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher par marque, modèle, ville..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-700/80 border border-slate-600/50 text-white rounded-2xl px-6 py-4 pl-14 text-lg
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all duration-200"
              />
              <Car className="absolute left-5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
            <select 
              value={filters.city}
              onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
              className="bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3"
            >
              <option value="all">Toutes les villes</option>
              <option value="Alger">Alger</option>
              <option value="Oran">Oran</option>
              <option value="Constantine">Constantine</option>
            </select>

            <select 
              value={filters.category}
              onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              className="bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3"
            >
              <option value="all">Toutes catégories</option>
              <option value="Sedan">Berline</option>
              <option value="SUV">SUV</option>
              <option value="Compact">Compact</option>
            </select>

            <select 
              value={filters.priceRange}
              onChange={(e) => setFilters(prev => ({ ...prev, priceRange: e.target.value }))}
              className="bg-slate-700/50 border border-slate-600/50 text-white rounded-xl px-4 py-3"
            >
              <option value="all">Tous budgets</option>
              <option value="0-4000">Moins de 4,000 DZD</option>
              <option value="4000-6000">4,000 - 6,000 DZD</option>
              <option value="6000-10000">Plus de 6,000 DZD</option>
            </select>
          </div>

          <div className="text-center mt-4">
            <p className="text-slate-400">{filteredVehicles.length} véhicule(s) trouvé(s)</p>
          </div>
        </div>

        <div className="flex space-x-1 overflow-x-auto pb-4 mb-8 -mx-4 px-4">
          {[
            { id: 'discover', label: 'Découvrir', icon: Car },
            { id: 'history', label: 'Mes réservations', icon: History },
            { id: 'profile', label: 'Mon profil', icon: User }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-xl font-medium transition-all duration-200 
                        whitespace-nowrap flex-shrink-0
                ${activeView === tab.id 
                  ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-lg' 
                  : 'text-slate-300 hover:text-white hover:bg-slate-700/50 border border-slate-600/30'
                }`}
            >
              <tab.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {activeView === 'discover' && (
          <div>
            {filteredVehicles.length === 0 ? (
              <div className="text-center py-16 bg-slate-800/50 rounded-2xl border border-slate-700/50">
                <Car className="h-16 w-16 text-slate-500 mx-auto mb-4" />
                <h3 className="text-white text-lg font-semibold mb-2">Aucun véhicule trouvé</h3>
                <p className="text-slate-400 mb-6">Essayez de modifier vos critères de recherche</p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilters({ city: 'all', category: 'all', priceRange: 'all' });
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
                >
                  Voir tous les véhicules
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredVehicles.map(vehicle => (
                  <VehicleCard 
                    key={vehicle.id} 
                    vehicle={vehicle}
                    onReserve={(v) => {
                      setSelectedVehicle(v);
                      setShowReservationModal(true);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeView === 'history' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white">Mes Réservations</h2>
                <p className="text-slate-300">Historique de vos locations</p>
              </div>
            </div>

            <div className="space-y-4">
              {reservations.length === 0 ? (
                <div className="text-center py-16 bg-slate-800/50 rounded-2xl border border-slate-700/50">
                  <Calendar className="h-16 w-16 text-slate-500 mx-auto mb-4" />
                  <h3 className="text-white text-lg font-semibold mb-2">Aucune réservation</h3>
                  <p className="text-slate-400 mb-6">Vous n'avez pas encore effectué de réservation</p>
                  <button
                    onClick={() => setActiveView('discover')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
                  >
                    Découvrir les véhicules
                  </button>
                </div>
              ) : (
                reservations.map(reservation => (
                  <ReservationCard key={reservation.id} reservation={reservation} />
                ))
              )}
            </div>
          </div>
        )}

        {activeView === 'profile' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/50 rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-white mb-6">Mon Profil</h2>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-slate-400 text-sm">Nom complet</label>
                    <p className="text-white font-medium">{user?.full_name}</p>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-slate-400 text-sm">Email</label>
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{user?.email}</p>
                      {user?.email_verified && <CheckCircle className="h-4 w-4 text-emerald-400" />}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-emerald-500/10 border border-emerald-400/20 rounded-xl p-4 text-center">
                    <Calendar className="h-6 w-6 text-emerald-400 mx-auto mb-2" />
                    <p className="text-white text-lg font-bold">{userStats.total_reservations || 0}</p>
                    <p className="text-emerald-300 text-sm">Réservations</p>
                  </div>
                  
                  <div className="bg-purple-500/10 border border-purple-400/20 rounded-xl p-4 text-center">
                    <DollarSign className="h-6 w-6 text-purple-400 mx-auto mb-2" />
                    <p className="text-white text-lg font-bold">{Math.round((userStats.total_spent || 0) / 1000)}K</p>
                    <p className="text-purple-300 text-sm">DZD dépensés</p>
                  </div>
                  
                  <div className="bg-blue-500/10 border border-blue-400/20 rounded-xl p-4 text-center">
                    <Clock className="h-6 w-6 text-blue-400 mx-auto mb-2" />
                    <p className="text-white text-lg font-bold">{userStats.days_member || 0}</p>
                    <p className="text-blue-300 text-sm">Jours membre</p>
                  </div>
                  
                  <div className="bg-amber-500/10 border border-amber-400/20 rounded-xl p-4 text-center">
                    <Star className="h-6 w-6 text-amber-400 mx-auto mb-2" />
                    <p className="text-white text-lg font-bold">{userStats.average_rating || '—'}</p>
                    <p className="text-amber-300 text-sm">Note moy.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ReservationModal
        vehicle={selectedVehicle}
        isOpen={showReservationModal}
        onClose={() => setShowReservationModal(false)}
        onConfirm={fetchClientData}
      />
    </div>
  );
};

// AppContent Component
const AppContent = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-400 mx-auto mb-4"></div>
          <p className="text-white text-xl">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user && window.location.pathname === '/reset-password') {
    return <PasswordResetPage />;
  }

  if (!user) {
    return <LoginPage />;
  }

  return (
    <Routes>
      <Route 
        path="/" 
        element={
          user.role === 'admin' ? <AdminDashboard /> :
          user.role === 'agency_owner' ? <AgencyDashboard /> :
          <ClientDashboard />
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App Component
const App = () => {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
};

export default App;